import React, { useCallback, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, RefreshControl, Pressable } from 'react-native';
import { router } from 'expo-router';
import { ClipboardList, Plus, Home, Building2, Landmark } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  ELEVATION,
  CONTENT_MAX_WIDTH,
} from '../../src/theme';
import { Header } from '@components/layout';
import { EmptyState, Skeleton } from '@components/common';
import { useAuthStore } from '../../src/store/authStore';
import { useAgentChecklistsStore } from '../../src/store/agentChecklistsStore';
import { useAgentClientsStore } from '../../src/store/agentClientsStore';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { formatRelativeTime } from '../../src/utils/time';
import { InsuranceChecklist, StakeholderType } from '../../src/types';

const STAKEHOLDER_ICON: Record<StakeholderType, typeof Home> = {
  homeowner: Home,
  business: Building2,
  municipality: Landmark,
};

const STAKEHOLDER_LABEL: Record<StakeholderType, string> = {
  homeowner: 'Homeowner',
  business: 'Business',
  municipality: 'Municipality',
};

export default function AgentChecklistsScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user } = useAuthStore();
  const isDesktopShell = useDesktopShell();
  const { checklists, isLoading, fetchChecklists } = useAgentChecklistsStore();
  const clients = useAgentClientsStore((s) => s.clients);
  const fetchClients = useAgentClientsStore((s) => s.fetchClients);

  const insurerName = user?.agentProfile?.insurerName;

  const load = useCallback(async () => {
    if (!insurerName) return;
    await fetchChecklists(insurerName);
  }, [insurerName, fetchChecklists]);

  useEffect(() => {
    load();
    // "Used by N properties" derives from the shared clients fetch — the
    // store's staleness gate makes this free when coming from the hub.
    if (insurerName) void fetchClients(insurerName);
  }, [load, insurerName, fetchClients]);

  // checklistId → number of claims using it. Zero extra reads.
  const usageById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const c of clients) {
      const cid = c.claim.insurerChecklistId;
      if (cid) map[cid] = (map[cid] ?? 0) + 1;
    }
    return map;
  }, [clients]);

  if (!user?.agentProfile) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Header showBack title="Checklists" />
        <EmptyState
          icon={ClipboardList}
          title="Not an insurance agent"
          message="Your account doesn't have agent access."
        />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header
        showBack
        showAvatar={false}
        large
        title="Checklists"
        // Desktop shell: the sidebar heads the agent section with this same
        // insurer name, so repeating it here is duplicated chrome — and the
        // second line pushed this header past SHELL_HEADER_HEIGHT, leaving its
        // rule out of line with the sidebar's. Mobile has no sidebar and keeps it.
        subtitle={isDesktopShell ? undefined : insurerName}
        rightLabel={isDesktopShell ? 'New checklist' : undefined}
        onRightPress={
          isDesktopShell
            ? () => router.push({ pathname: '/agent/checklists/[id]', params: { id: 'new' } })
            : undefined
        }
      />
      {isLoading && checklists.length === 0 ? (
        <View style={styles.listContent}>
          {[0, 1, 2].map((i) => (
            <View key={i} style={[styles.card, { borderBottomColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <Skeleton width={16} height={16} borderRadius={BORDER_RADIUS.sm} />
                <View style={{ flex: 1 }}>
                  <Skeleton width={90} height={13} />
                </View>
                <Skeleton width={48} height={13} />
              </View>
              <Skeleton width="55%" height={16} />
              <Skeleton width="35%" height={13} />
            </View>
          ))}
        </View>
      ) : (
        <FlatList
          data={checklists}
          keyExtractor={(c) => c.id}
          contentContainerStyle={[
            styles.listContent,
            { paddingBottom: insets.bottom + 80 },
            checklists.length === 0 && styles.listEmpty,
          ]}
          refreshControl={
            <RefreshControl refreshing={isLoading && checklists.length > 0} onRefresh={load} />
          }
          ListEmptyComponent={
            <EmptyState
              icon={ClipboardList}
              title="No checklists yet"
              message="Tap + to create your first checklist."
            />
          }
          renderItem={({ item }) => (
            <ChecklistCard
              checklist={item}
              usedByCount={usageById[item.id] ?? 0}
              onPress={() =>
                router.push({ pathname: '/agent/checklists/[id]', params: { id: item.id } })
              }
            />
          )}
        />
      )}
      {!isDesktopShell && (
        <Pressable
          style={[styles.fab, { backgroundColor: colors.action }]}
          onPress={() => router.push({ pathname: '/agent/checklists/[id]', params: { id: 'new' } })}
          accessibilityRole="button"
          accessibilityLabel="Create new checklist">
          <Plus size={24} color={colors.actionOn} />
        </Pressable>
      )}
    </View>
  );
}

function ChecklistCard({
  checklist,
  usedByCount,
  onPress,
}: {
  checklist: InsuranceChecklist;
  usedByCount: number;
  onPress: () => void;
}) {
  const { colors } = useTheme();
  const Icon = STAKEHOLDER_ICON[checklist.stakeholderType];
  const thresholds = checklist.reportThresholds ?? [];
  const updated = formatRelativeTime(checklist.updatedAt, { suffix: 'ago' });

  return (
    <Pressable
      style={[styles.card, { borderBottomColor: colors.border }]}
      onPress={onPress}
      testID="agent-checklist-card"
      accessibilityRole="button"
      accessibilityLabel={`${checklist.insurerName}, ${STAKEHOLDER_LABEL[checklist.stakeholderType]} checklist, ${checklist.items.length} ${checklist.items.length === 1 ? 'item' : 'items'}`}>
      <View style={styles.cardHeader}>
        <Icon size={16} color={colors.textSecondary} />
        <Text style={[styles.stakeholderLabel, { color: colors.textSecondary }]}>
          {STAKEHOLDER_LABEL[checklist.stakeholderType]}
        </Text>
        {/* Content, not a hint: textTertiary is sub-AA by design (2.72:1 at
            12px on white) and axe flagged both these lines. */}
        <Text style={[styles.itemCount, { color: colors.textSecondary }]}>
          {checklist.items.length} {checklist.items.length === 1 ? 'item' : 'items'}
        </Text>
      </View>
      <Text style={[styles.cardTitle, { color: colors.text }]}>{checklist.insurerName}</Text>
      <Text testID="checklist-meta" style={[styles.cardMeta, { color: colors.textSecondary }]}>
        {[
          thresholds.length > 0 ? `Auto-report at ${thresholds.join('%, ')}%` : null,
          usedByCount > 0
            ? `Used by ${usedByCount} ${usedByCount === 1 ? 'property' : 'properties'}`
            : null,
          updated ? `Updated ${updated}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  listContent: {
    padding: SPACING.md,
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
    alignSelf: 'center',
    // No gap: the rows are separated by their own hairline, and a gap would
    // leave each rule floating between two bands of canvas.
  },
  listEmpty: { flexGrow: 1, justifyContent: 'center' },
  // Hairline rows on canvas, not filled cards.
  card: {
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  stakeholderLabel: {
    ...TYPOGRAPHY.label,
    flex: 1,
  },
  itemCount: {
    ...TYPOGRAPHY.bodySmall,
  },
  cardTitle: {
    ...TYPOGRAPHY.button,
  },
  cardMeta: {
    ...TYPOGRAPHY.bodySmall,
  },
  fab: {
    position: 'absolute',
    right: SPACING.md,
    bottom: 96,
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    ...ELEVATION.floating,
  },
});
